import Sheet from "components/sheet/Sheet";
import React, { useEffect, useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import "./selectDynamicComboSheet.scss";
import { IProduct, IVariant, TComboVariants } from "redux/reducers/models/product";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { ICartLineItem, IUpdatedDynamicComboSelection } from "redux/reducers/models/cart";
import axios, { CancelToken } from "axios";
import { GetComboVariants } from "api/rpc/2024-04/facilityAdmin/product/product";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { StatusCode } from "api/protocols";
import Spin from "components/spin/spin";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";

interface ISelectDynamicComboSheetProps {
  open: boolean;
  onOk: (dynamicComboSelections: IUpdatedDynamicComboSelection[]) => void;
  onCancel: () => void;
  dynamicCombo?: IProduct;
  dynamicComboVariant?: IVariant;
  dynamicComboLineItem?: ICartLineItem;
}

interface ISelectDynamicComboSheetContent {
  title: string;
  dynamicComboQuantity: number;
}

interface ISelectDynamicComboSheetState {
  comboVariants: TComboVariants[];
  loadingComboVariants: boolean;
  dynamicComboSelections: IUpdatedDynamicComboSelection[];
}

const initialState: ISelectDynamicComboSheetState = {
  comboVariants: [],
  loadingComboVariants: false,
  dynamicComboSelections: [],
};

export default function SelectDynamicComboSheet(props: ISelectDynamicComboSheetProps) {
  const { open, onOk, onCancel, dynamicCombo, dynamicComboVariant, dynamicComboLineItem } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [state, setState] = useState<ISelectDynamicComboSheetState>(cloneDeep(initialState));

  const isEditingLineItem = !!dynamicComboLineItem;

  const dynamicComboContent = useMemo<ISelectDynamicComboSheetContent>(() => {
    if (dynamicCombo && dynamicComboVariant) {
      return {
        title: dynamicCombo.preferred_title || dynamicCombo.title,
        dynamicComboQuantity: dynamicCombo.dynamic_combo_quantity,
      };
    } else if (dynamicComboLineItem) {
      return {
        title:
          dynamicComboLineItem.preferred_title ||
          dynamicComboLineItem.product?.preferred_title ||
          dynamicComboLineItem.product?.title,
        dynamicComboQuantity: dynamicComboLineItem.product?.dynamic_combo_quantity,
      };
    } else {
      return {
        title: "",
        dynamicComboQuantity: null,
      };
    }
  }, [dynamicCombo, dynamicComboVariant, dynamicComboLineItem]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (open) {
      const baseVariantId = dynamicComboVariant?.id ?? dynamicComboLineItem?.variant_id;

      if (dynamicComboLineItem) {
        setState(prev => ({
          ...prev,
          dynamicComboSelections:
            dynamicComboLineItem.dynamic_combo_selections?.map(dynamicComboSelection => {
              return {
                id: dynamicComboSelection.variant?.id,
                quantity: dynamicComboSelection.quantity,
              };
            }) ?? [],
        }));
      }

      if (baseVariantId) {
        void loadDynamicComboVariants(baseVariantId, source.token);
      }
    } else {
      setState(cloneDeep(initialState));
    }

    return () => source.cancel();
  }, [open, dynamicCombo, dynamicComboVariant, dynamicComboLineItem]);

  async function loadDynamicComboVariants(baseVariantId: number, cancelToken: CancelToken) {
    try {
      setState(prev => ({ ...prev, loadingComboVariants: true }));

      const getComboVariantsRes = await GetComboVariants({ base_variant_id: baseVariantId }, false, cancelToken);

      if (cancelToken?.reason) {
        return;
      }

      if (getComboVariantsRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading combo variants")));
        setState(prev => ({ ...prev, comboVariants: [] }));
      } else {
        setState(prev => ({ ...prev, comboVariants: getComboVariantsRes.data.combination_variants }));
      }
    } finally {
      setState(prev => ({ ...prev, loadingComboVariants: false }));
    }
  }

  const selectedVariantQuantity = useMemo(() => {
    return state.dynamicComboSelections?.reduce((accumulator, next) => accumulator + next.quantity, 0) ?? 0;
  }, [state.dynamicComboSelections]);

  const correctQuantitySelected = selectedVariantQuantity === dynamicComboContent.dynamicComboQuantity;
  const quantitySelectionAvailable = selectedVariantQuantity < dynamicComboContent.dynamicComboQuantity;

  function addVariantSelection(variant: TComboVariants) {
    if (quantitySelectionAvailable) {
      setState(prev => {
        const existingSelectedVariant = prev.dynamicComboSelections.find(
          selectedVariant => selectedVariant.id === variant.id,
        );

        if (existingSelectedVariant) {
          return {
            ...prev,
            dynamicComboSelections: prev.dynamicComboSelections.map(selectedVariant => {
              if (selectedVariant.id === variant.id) {
                return {
                  ...selectedVariant,
                  quantity: selectedVariant.quantity + 1,
                };
              } else {
                return selectedVariant;
              }
            }),
          };
        } else {
          return {
            ...prev,
            dynamicComboSelections: [...prev.dynamicComboSelections, { id: variant.id, quantity: 1 }],
          };
        }
      });
    }
  }

  function removeVariantSelection(variant: TComboVariants) {
    if (selectedVariantQuantity > 0) {
      setState(prev => {
        const existingSelectedVariant = prev.dynamicComboSelections.find(
          selectedVariant => selectedVariant.id === variant.id,
        );

        if (existingSelectedVariant?.quantity == null) {
          return prev;
        } else if (existingSelectedVariant.quantity <= 1) {
          return {
            ...prev,
            dynamicComboSelections: prev.dynamicComboSelections.filter(
              selectedVariant => selectedVariant.id !== variant.id,
            ),
          };
        } else {
          return {
            ...prev,
            dynamicComboSelections: prev.dynamicComboSelections.map(selectedVariant => {
              if (selectedVariant.id === variant.id) {
                return {
                  ...selectedVariant,
                  quantity: selectedVariant.quantity - 1,
                };
              } else {
                return selectedVariant;
              }
            }),
          };
        }
      });
    }
  }

  function clearSelections() {
    setState(prev => ({ ...prev, dynamicComboSelections: [] }));
  }

  function handleUpdateChanges() {
    onOk(state.dynamicComboSelections);
  }

  return (
    <Sheet
      open={open}
      size="small"
      title={t("Select products")}
      supportText={t("Choose products to add it in the combo.")}
      onCancel={onCancel}
      footer={null}
      closable
      backDropCancel={false}
    >
      <div className="select-dynamic-combo-sheet-header">
        <div className="select-dynamic-combo-sheet-header-heading">{dynamicComboContent.title}</div>
        <div className="select-dynamic-combo-sheet-header-selected">
          {`${selectedVariantQuantity}/${dynamicComboContent.dynamicComboQuantity} ${t("selected")}`}
        </div>
      </div>

      {state.loadingComboVariants ? (
        <div className="select-dynamic-combo-sheet-loading">
          <div className="select-dynamic-combo-sheet-loading-spin-container">
            <Spin />
          </div>
          <div>{t("Loading combo products...")}</div>
        </div>
      ) : (
        <div className="select-dynamic-combo-sheet-variants">
          {state.comboVariants.map(comboVariant => {
            const selectedVariant = state.dynamicComboSelections.find(sv => sv.id === comboVariant.id);
            const productTitle = comboVariant.product?.preferred_title || comboVariant.product?.title;

            return (
              <div key={comboVariant.id} className="select-dynamic-combo-sheet-variant">
                <div
                  className="variant-content"
                  style={{
                    backgroundColor: comboVariant.product?.meta?.button_color,
                    cursor: !selectedVariant && quantitySelectionAvailable ? "pointer" : "default",
                  }}
                  onClick={
                    !selectedVariant && quantitySelectionAvailable ? () => addVariantSelection(comboVariant) : undefined
                  }
                >
                  <div className="variant-content-titles">
                    <span className="variant-content-title">{comboVariant.title}</span>
                    {productTitle && productTitle !== comboVariant.title ? (
                      <span className="variant-content-product-title">{productTitle}</span>
                    ) : null}
                  </div>

                  {selectedVariant?.quantity != null && selectedVariant.quantity > 0 && (
                    <div className="variant-content-quantity-selection">
                      <FontAwesomeIcon
                        className="variant-content-quantity-selection-icon"
                        icon={faMinus}
                        onClick={e => {
                          e.stopPropagation();
                          removeVariantSelection(comboVariant);
                        }}
                      />
                      <p>{selectedVariant?.quantity}</p>
                      <FontAwesomeIcon
                        className={classNames("variant-content-quantity-selection-icon", {
                          "variant-content-quantity-selection-icon-disabled": !quantitySelectionAvailable,
                        })}
                        icon={faPlus}
                        onClick={e => {
                          e.stopPropagation();
                          if (quantitySelectionAvailable) {
                            addVariantSelection(comboVariant);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="select-dynamic-combo-sheet-footer">
        <Button
          type="text"
          onClick={clearSelections}
          disabled={state.dynamicComboSelections == null || state.dynamicComboSelections.length === 0}
        >
          {t("Clear")}
        </Button>
        <div className="select-dynamic-combo-sheet-footer-actions">
          <Button type="secondary" onClick={onCancel}>
            {t("Go back")}
          </Button>
          <Button type="primary" onClick={handleUpdateChanges} disabled={!correctQuantitySelected}>
            {isEditingLineItem ? t("Update changes") : t("Continue")}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
